import { useTranslation } from 'react-i18next'
import { Chart } from 'react-chartjs-2'
import { Box, Typography, useTheme } from '@mui/material'
import { DetailedDescription, DetailedGapDescription } from '../../shared/model/models'
import { DiversityDimenstionConfig, ModuleConfig } from '../../shared/model'
import { translatedDomainName, drawExtraLine } from '../../shared/utils'

type FsDescriptionByDiversityProps = {
  detailedGapDescription: DetailedGapDescription
  moduleConfig?: ModuleConfig
  diversityDimenstionConfig?: DiversityDimenstionConfig
  groupOfInterestPercentage: number
}

interface DescriptionDataItem {
  referenceGroupName: string
  groupOfInterestName: string
  descriptiveName: string
  referenceGroupCount: number
  groupOfInterestCount: number
}

const FsDescriptionByDiversity = (props: FsDescriptionByDiversityProps) => {
  const { t, i18n } = useTranslation()
  const {
    detailedGapDescription,
    moduleConfig,
    diversityDimenstionConfig,
    groupOfInterestPercentage,
  } = props
  const descriptionDataItems: DescriptionDataItem[] = []
  const groupOfInterestObservations: number[] = []

  const theme = useTheme()

  detailedGapDescription.groupOfInterest.descriptions.forEach((groupOfInterestData) => {
    groupOfInterestObservations.push(
      groupOfInterestData.descriptionDependentVariable.numberOfObservations,
    )
  })

  const goiLabel = translatedDomainName(
    `${detailedGapDescription.groupOfInterest.groupVariable.label}`,
    t,
  )
  const rgLabel = translatedDomainName(
    `${detailedGapDescription.referenceGroup.groupVariable.label}`,
    t,
  )

  detailedGapDescription.referenceGroup.descriptions.forEach(
    (referenceGroupData: DetailedDescription, index: number) => {
      descriptionDataItems.push({
        referenceGroupName: rgLabel,
        groupOfInterestName: goiLabel,
        descriptiveName:
          translatedDomainName(`${referenceGroupData.descriptiveVariable.label}`, t)
            .charAt(0)
            .toUpperCase() +
          translatedDomainName(`${referenceGroupData.descriptiveVariable.label}`, t).slice(1),
        referenceGroupCount: referenceGroupData.descriptionDependentVariable.numberOfObservations,
        groupOfInterestCount: groupOfInterestObservations[index as number]!,
      })
    },
  )

  const descriptiveVariableName = t(
    `descriptiveCharacteristic.name.${detailedGapDescription?.groupOfInterest.descriptiveCharacteristic.name}` as any,
  )

  const shareOfGOI = descriptionDataItems.map((item) =>
    Number(
      (item.groupOfInterestCount * 100) / (+item.groupOfInterestCount + +item.referenceGroupCount),
    ),
  )

  const shareOfRG = descriptionDataItems.map((item) =>
    Number(
      (item.referenceGroupCount * 100) / (+item.groupOfInterestCount + +item.referenceGroupCount),
    ),
  )

  const helpTextHtml =
    detailedGapDescription &&
    detailedGapDescription.gapCauseDescriptions.filter((info) => info.locale === i18n.language)
      ? detailedGapDescription.gapCauseDescriptions.filter(
          (info) => info.locale === i18n.language,
        )[0].gapEmpiricalDiversity
      : ''

  return (
    <div
      style={{
        height: '100%',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        py={2}
        height={600}
        width={descriptionDataItems.length * 250}
        maxWidth="100%"
        margin="auto"
      >
        <Chart
          type="bar"
          id={`description_diversity_bar_chart_${moduleConfig?.module}`}
          data={{
            labels: descriptionDataItems.map((tableData) => tableData.descriptiveName),
            datasets: [
              {
                label: translatedDomainName(`${descriptionDataItems[0].groupOfInterestName}`, t),
                backgroundColor: diversityDimenstionConfig?.fsColor?.primary,
                data: shareOfGOI.map((item) => `${item.toFixed(1)}`),
              },
              {
                label: t(`${descriptionDataItems[0].referenceGroupName}` as any),
                backgroundColor: theme.palette.divider,
                data: shareOfRG.map((item) => item.toFixed(1)),
                borderRadius: 8
              },
            ],
          }}
          options={{
            locale: i18n.language,
            maintainAspectRatio: false,
            layout: {
              padding: {
                right: 150,
              },
            },
            plugins: {
              legend: {
                position: 'top',
                onHover: (event) => {
                  // @ts-ignore
                  if (event.native?.target) event.native.target.style.cursor = 'pointer'
                },
                title: {
                  display: true,
                  text: t(
                    `context.model.name.${
                      detailedGapDescription?.groupOfInterest.groupVariable.name.split('@')[0]
                    }` as any,
                  ),
                  font: {
                    weight: 'bold',
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (context) => `${context.dataset.label}: ${context.formattedValue} %`,
                },
              },
              datalabels: {
                anchor: 'end',
                align: 'end',
                display: 'auto',
                color: theme.palette.text.secondary,
                formatter: (value) =>
                  value !== '0.0'
                    ? `${Number(value).toLocaleString(i18n.language, {
                        maximumFractionDigits: 1,
                        minimumFractionDigits: 1,
                      })} %`
                    : '',
              },
              annotation: {
                clip: false,
                annotations: {
                  line1: {
                    type: 'line',
                    beforeDraw: drawExtraLine,
                    value: groupOfInterestPercentage,
                    scaleID: 'y',
                    borderColor: diversityDimenstionConfig?.fsColor.colors[2],
                    borderWidth: 2,
                    borderDash: [6, 6],
                    borderDashOffset: 0,
                    label: {
                      backgroundColor: diversityDimenstionConfig?.fsColor.colors[2],
                      color: 'black',
                      font: {
                        size: 14,
                        weight: 'normal',
                      },
                      display: true,
                      content: [
                        goiLabel.length > 20 ? `${goiLabel.slice(0, 20)}...` : goiLabel,
                        `${t('total')} (${groupOfInterestPercentage.toLocaleString(i18n.language, {
                          maximumFractionDigits: 1,
                        })} %)`,
                      ],
                      position: 'end',
                      textAlign: 'left',
                      xAdjust: 150,
                      yAdjust: 0,
                    },
                  },
                },
              },
            },
            scales: {
              x: {
                stacked: true,
                title: {
                  display: true,
                  text: descriptiveVariableName,
                  padding: 20,
                  font: {
                    weight: 'bold',
                  },
                },
                grid: {
                  display: false,
                },
                border: {
                  color: theme.palette.text.disabled,
                },
                ticks: {
                  autoSkip: false,
                  color: theme.palette.text.primary,
                  callback: (value) =>
                    descriptionDataItems[Number(value)]
                      ? descriptionDataItems[Number(value)].descriptiveName.length > 25
                        ? `${descriptionDataItems[Number(value)].descriptiveName.slice(0, 25)}...`
                        : `${descriptionDataItems[Number(value)].descriptiveName}`
                      : '',
                },
              },
              y: {
                stacked: true,
                grid: {
                  color: theme.palette.divider,
                },
                border: {
                  display: false,
                },
                min: 0,
                max: 100,
                ticks: {
                  stepSize: 20,
                  color: theme.palette.text.primary,
                  callback: (value) => `${value} %`,
                },
              },
            },
          }}
        />
      </Box>
      <Box>
        <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
          {helpTextHtml}
        </Typography>
      </Box>
    </div>
  )
}

export default FsDescriptionByDiversity
